<script lang="ts">
  /**
   * Searchable, multi-select list of projects to place on the board.
   *
   * Selection is intentionally separate from the commit: filters and searches
   * can be changed without losing the projects selected so far.
   */
  import type { ProjectCategory, ProjectStatus } from '../../lib/types';
  import { categoryIcons } from '../../lib/utils';

  interface PickerProject {
    id: string;
    title: string;
    category: ProjectCategory;
    status: ProjectStatus;
    isPriority: boolean;
    monthKey: string | null;
  }

  interface SearchResponse {
    projects: PickerProject[];
    total: number;
    limit: number;
  }

  interface FilterPreset {
    label: string;
    params: Record<string, string>;
  }

  interface Props {
    /** Projects already on the board — shown as disabled so they aren't added twice. */
    placedProjectIds: Set<string>;
    onAdd: (projectIds: string[]) => Promise<void>;
    onClose: () => void;
  }

  let { placedProjectIds, onAdd, onClose }: Props = $props();

  const currentMonth = new Date().toISOString().slice(0, 7);
  const filters: FilterPreset[] = [
    { label: 'All scheduled', params: { scheduling_state: 'scheduled' } },
    { label: 'This month', params: { month: currentMonth } },
    { label: 'Active', params: { status: 'active' } },
    { label: 'Backlog', params: { scheduling_state: 'backlog' } },
    { label: 'Priority', params: { priority: 'true' } },
  ];

  let query = $state('');
  let activeFilter = $state<string | null>(null);
  let filterParams = $state<Record<string, string>>({});
  let results = $state<PickerProject[]>([]);
  let total = $state(0);
  let limit = $state(100);
  let selectedIds = $state<Set<string>>(new Set());
  let loading = $state(true);
  let committing = $state(false);
  let errorMessage = $state<string | null>(null);
  let searchInput = $state<HTMLInputElement | undefined>();
  let requestVersion = 0;
  let selectFilterMatchesOnNextLoad = false;

  let selectableResults = $derived(results.filter((project) => !placedProjectIds.has(project.id)));
  let selectedCount = $derived(selectedIds.size);
  let isTruncated = $derived(total > results.length);

  async function load(
    q: string,
    params: Record<string, string> = filterParams,
    selectMatches = false,
  ) {
    const version = ++requestVersion;
    loading = true;
    errorMessage = null;

    try {
      const searchParams = new URLSearchParams(params);
      if (q) searchParams.set('q', q);
      // The endpoint caps results at 500; disclose anything beyond that rather
      // than pretending that a preset selected every matching project.
      searchParams.set('limit', '500');
      const encodedParams = searchParams.toString();
      const response = await fetch(`/projects/search?${encodedParams}`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Search failed');
      const payload: SearchResponse = await response.json();
      if (version !== requestVersion) return;
      results = payload.projects;
      total = payload.total;
      limit = payload.limit;
      if (selectMatches) {
        const nextSelection = new Set(selectedIds);
        for (const project of payload.projects) {
          if (!placedProjectIds.has(project.id)) nextSelection.add(project.id);
        }
        selectedIds = nextSelection;
      }
    } catch {
      if (version !== requestVersion) return;
      errorMessage = 'Could not load projects.';
      results = [];
      total = 0;
    } finally {
      if (version === requestVersion) loading = false;
    }
  }

  // Debounced search on query change (also performs the initial load).
  // eslint-disable-next-line momentium/no-unguarded-effect -- debounced external fetch keyed on query, returns cleanup
  $effect(() => {
    const q = query;
    const params = filterParams;
    const timer = setTimeout(
      () => {
        const selectMatches = selectFilterMatchesOnNextLoad;
        selectFilterMatchesOnNextLoad = false;
        void load(q, params, selectMatches);
      },
      q ? 200 : 0,
    );
    return () => clearTimeout(timer);
  });

  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM focus on mount
  $effect(() => {
    searchInput?.focus();
  });

  function toggleSelection(projectId: string) {
    if (placedProjectIds.has(projectId)) return;
    const next = new Set(selectedIds);
    if (next.has(projectId)) next.delete(projectId);
    else next.add(projectId);
    selectedIds = next;
  }

  function selectAllVisible() {
    const next = new Set(selectedIds);
    for (const project of selectableResults) next.add(project.id);
    selectedIds = next;
  }

  function clearVisible() {
    const visibleIds = new Set(selectableResults.map((project) => project.id));
    selectedIds = new Set([...selectedIds].filter((id) => !visibleIds.has(id)));
  }

  function chooseFilter(filter: FilterPreset) {
    activeFilter = filter.label;
    filterParams = filter.params;
    selectFilterMatchesOnNextLoad = true;
  }

  async function commitSelection() {
    if (selectedIds.size === 0 || committing) return;
    committing = true;
    const committedIds = new Set(selectedIds);
    try {
      await onAdd([...committedIds]);
      // Keep anything selected while the request was in flight. Only the IDs
      // included in this commit should be removed from the pending selection.
      selectedIds = new Set([...selectedIds].filter((id) => !committedIds.has(id)));
    } finally {
      committing = false;
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="picker" onkeydown={handleKeydown}>
  <div class="picker-header">
    <input
      autocomplete="off"
      bind:this={searchInput}
      bind:value={query}
      class="picker-search"
      type="search"
      placeholder="Search projects…"
      aria-label="Search projects to add"
    />
    <button class="picker-close" type="button" onclick={onClose} aria-label="Close picker">×</button
    >
  </div>

  <div class="picker-filters" aria-label="Quick filters">
    {#each filters as filter}
      <button
        class:active={activeFilter === filter.label}
        class="filter-chip"
        type="button"
        onclick={() => chooseFilter(filter)}
      >
        {filter.label}
      </button>
    {/each}
  </div>

  <div class="picker-results" role="group" aria-label="Projects">
    {#if loading}
      <div class="picker-empty">Loading…</div>
    {:else if errorMessage}
      <div class="picker-empty error">{errorMessage}</div>
    {:else if results.length === 0}
      <div class="picker-empty">No projects found.</div>
    {:else}
      {#each results as project (project.id)}
        {@const placed = placedProjectIds.has(project.id)}
        <label class:placed class="picker-item">
          <input
            type="checkbox"
            aria-label={project.title}
            checked={selectedIds.has(project.id)}
            disabled={placed}
            onchange={() => toggleSelection(project.id)}
          />
          <span class="picker-icon">{categoryIcons[project.category] ?? '📋'}</span>
          <span class="picker-title">{project.title}</span>
          {#if placed}
            <span class="picker-badge">on board</span>
          {/if}
        </label>
      {/each}
    {/if}
  </div>

  <div class="picker-footer">
    <div class="picker-count">
      {#if isTruncated}
        Showing {results.length} of {total} (limit {limit})
      {:else}
        {results.length} shown
      {/if}
    </div>
    <button
      class="select-all"
      type="button"
      onclick={selectedCount === selectableResults.length ? clearVisible : selectAllVisible}
      disabled={selectableResults.length === 0}
    >
      {selectedCount === selectableResults.length ? 'Clear visible' : 'Select all'}
    </button>
    <button
      class="add-button"
      type="button"
      onclick={commitSelection}
      disabled={selectedCount === 0 || committing}
    >
      {committing ? 'Adding…' : `Add ${selectedCount}`}
    </button>
  </div>
</div>

<style>
  .picker {
    width: 21rem;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }

  .picker-header {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem;
    border-bottom: 1px solid #f3f4f6;
  }
  .picker-search {
    flex: 1;
    min-width: 0;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    font-family: inherit;
  }
  .picker-search:focus {
    outline: none;
    border-color: #2563eb;
  }
  .picker-close {
    border: none;
    background: none;
    font-size: 1rem;
    line-height: 1;
    color: #9ca3af;
    cursor: pointer;
    padding: 0 0.125rem;
  }
  .picker-close:hover {
    color: #374151;
  }

  .picker-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    border-bottom: 1px solid #f3f4f6;
  }
  .filter-chip,
  .select-all {
    border: 1px solid #e5e7eb;
    border-radius: 999px;
    background: white;
    color: #4b5563;
    cursor: pointer;
    font: inherit;
    font-size: 0.625rem;
    padding: 0.2rem 0.4rem;
  }
  .filter-chip:hover,
  .filter-chip.active {
    border-color: #93c5fd;
    background: #eff6ff;
    color: #1d4ed8;
  }

  .picker-results {
    max-height: 18rem;
    overflow-y: auto;
    padding: 0.25rem;
  }
  .picker-empty {
    padding: 0.75rem 0.5rem;
    font-size: 0.6875rem;
    color: #9ca3af;
  }
  .picker-empty.error {
    color: #dc2626;
  }
  .picker-item {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    width: 100%;
    box-sizing: border-box;
    padding: 0.375rem 0.5rem;
    border-radius: 0.25rem;
    text-align: left;
    font-size: 0.75rem;
    color: #1f2937;
    cursor: pointer;
  }
  .picker-item:hover:not(.placed) {
    background: #f3f4f6;
  }
  .picker-item.placed {
    color: #9ca3af;
    cursor: default;
  }
  .picker-item input {
    margin: 0;
    accent-color: #2563eb;
  }
  .picker-icon {
    flex-shrink: 0;
    font-size: 0.8125rem;
    line-height: 1;
  }
  .picker-title {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .picker-badge {
    flex-shrink: 0;
    font-size: 0.5625rem;
    color: #9ca3af;
    background: #f3f4f6;
    padding: 0.0625rem 0.25rem;
    border-radius: 0.1875rem;
  }

  .picker-footer {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.4rem 0.5rem;
    border-top: 1px solid #f3f4f6;
  }
  .picker-count {
    flex: 1;
    min-width: 0;
    color: #6b7280;
    font-size: 0.625rem;
  }
  .select-all {
    white-space: nowrap;
  }
  .select-all:disabled {
    cursor: default;
    opacity: 0.5;
  }
  .add-button {
    border: none;
    border-radius: 0.25rem;
    background: #2563eb;
    color: white;
    cursor: pointer;
    font: inherit;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.35rem 0.5rem;
    white-space: nowrap;
  }
  .add-button:hover:not(:disabled) {
    background: #1d4ed8;
  }
  .add-button:disabled {
    cursor: default;
    opacity: 0.5;
  }
</style>
